<script setup lang="ts">

import { twMerge } from 'tailwind-merge'
import FilledStepIcon from '@/components/icons/FilledStepIcon.vue'
import UnfilledStepIcon from '@/components/icons/UnfilledStepIcon.vue'
import type { OnboardingStep } from '@/types/onboarding'
import { useScreenSize } from '@/composables/helpers/screen-size'
import BaseButton from '@/components/base/BaseButton.vue'

defineProps<{
  steps: OnboardingStep[]
  currentStep: OnboardingStep
}>()

defineEmits<{
  (e: 'setStep', index: number): void
  (e: 'goNext'): void
}>()

const { isVerySmallPhoneHeight } = useScreenSize()
</script>

<template>
  <div
    class="flex-1 flex flex-col items-center justify-between h-full relative w-full overflow-hidden"
  >
    <div class="w-full flex flex-col text-center gap-y-3 h-[100px]">
      <!-- Fade transition for the title -->
      <transition
        name="fade"
        mode="out-in"
      >
        <h2
          v-if="currentStep.title"
          :key="currentStep.index"
          class="text-[32px] font-semibold leading-[32px] -tracking-[0.64px] whitespace-pre-line px-4"
        >
          {{ currentStep.title }}
        </h2>
      </transition>

      <!-- Fade transition for the description -->
      <transition
        name="fade"
        mode="out-in"
      >
        <p
          v-if="currentStep.description"
          :key="currentStep.index"
          :class="twMerge('text-base font-medium -tracking-[0.32px] text-light-white/70 px-4 min-[390px]:whitespace-pre-line',
                          currentStep.index !== 0 && 'min-[370px]:whitespace-pre-line' )"
        >
          {{ currentStep.description }}
        </p>
      </transition>
    </div>
    <div class="flex flex-1 items-center justify-center relative w-full">
      <transition-group
        name="slider"
        tag="div"
        class="absolute inset-0 flex items-center justify-center"
      >
        <div
          v-if="currentStep.index === 0"
          :key="currentStep.index"
          class="background-green-linear rounded-full size-80 overflow-hidden justify-center items-end relative"
        >
          <img
            src="@/assets/images/gifs/duck-waving.gif"
            class="size-[300px] absolute left-[18px] top-[45px]"
          >
        </div>

        <img
          v-if="currentStep.index !== 0 && currentStep.image"
          :key="currentStep.index"
          :src="currentStep.image"
          alt="Onboarding step"
          class="absolute left-0 w-full object-cover"
          :class="[isVerySmallPhoneHeight ? 'h-auto' : 'h-full top-0 bottom-0']"
        >
      </transition-group>
    </div>
    <div class="flex flex-col w-full items-center z-20 px-6 gap-y-6">
      <div class="flex gap-x-4">
        <component
          v-for="step in steps"
          :key="step.index"
          :is="step.index <= currentStep.index ? FilledStepIcon : UnfilledStepIcon"
          class="h-3 w-3 min-w-[12px] cursor-pointer"
          @click="$emit('setStep', step.index)"
        />
      </div>
      <BaseButton
        class="w-full"
        @click="$emit('goNext')"
      >
        {{ currentStep.buttonLabel ?? $t('onboarding.next', 'Next') }}
      </BaseButton>
    </div>
    <Transition
      name="fade-delayed"
      mode="out-in"
    >
      <div
        v-if="currentStep.index !== 0"
        class="bottom-shade absolute z-10 h-[50%] w-full bottom-0"
      />
    </Transition>
  </div>
</template>

<style scoped>
.bottom-shade {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 11.49%, #000 47.55%);
}

.background-green-linear {
  background: linear-gradient(180deg, #E2FFA5 0%, #C6F36B 98%);
}

/* Slider transition classes */
.slider-enter-active,
.slider-leave-active {
  transition: transform 0.35s ease;
}

.slider-enter-from {
  transform: translateX(100%);
}

.slider-leave-to {
  transform: translateX(-100%);
}

/* To ensure the leaving image stays in position while sliding */
.slider-leave-active {
  position: absolute;
}

/* Custom transition for the duck image on step 0 */
.slider-leave-active .background-green-linear {
  transition: transform 0.35s ease;
  transform: translateX(-100%);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}

.fade-delayed-enter-active,
.fade-delayed-leave-active {
  transition: opacity 0.35s ease;
  transition-delay: 0.15s; /* Add delay before the transition starts */
}

.fade-delayed-enter-from,
.fade-delayed-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}
</style>

